// src/pwa/hooks/usePwaAuth.js
import { useEffect, useMemo, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  onAuthStateChanged,
  signInWithCustomToken,
  signOut,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import { auth, functions } from '../../firebase/firebase';

// installId を端末に保存するキー
const SS_KEY_INSTALL_ID = 'pwa_install_id';

// 超軽量 UUID v4 生成（十分に一意でOK。必要ならより厳密な実装へ）
function uuidv4() {
  // 8-4-4-4-12
  const s = crypto.getRandomValues(new Uint8Array(16));
  s[6] = (s[6] & 0x0f) | 0x40; // version 4
  s[8] = (s[8] & 0x3f) | 0x80; // variant
  const toHex = (n) => n.toString(16).padStart(2, '0');
  const hex = Array.from(s, toHex).join('');
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
}

// 端末一意の installId を取得（なければ生成して保存）
function getOrCreateInstallId() {
  try {
    const saved = localStorage.getItem(SS_KEY_INSTALL_ID);
    if (saved) return saved;
    const id = uuidv4();
    localStorage.setItem(SS_KEY_INSTALL_ID, id);
    return id;
  } catch {
    // 例外時はメモリだけで運用（再起動で変わる）
    return uuidv4();
  }
}

/**
 * usePwaAuth
 * - 返り値: { uid, error }
 *   - uid === undefined … 判定中（ローディング）
 *   - uid === null      … 未ログイン（※通常この状態はすぐ埋まる想定）
 *   - uid === "PWA_..." … ログイン済み
 */
export default function usePwaAuth() {
  const [uid, setUid] = useState(undefined); // ローディング中は undefined
  const [error, setError] = useState(null);

  const installId = useMemo(getOrCreateInstallId, []);
  const exchangingRef = useRef(false); // 二重実行防止

  useEffect(() => {
    let unsub = () => {};
    (async () => {
      try {
        // PWA はローカル永続にしておく（アプリらしい挙動）
        await setPersistence(auth, browserLocalPersistence);
      } catch (e) {
        // 永続化失敗しても続行は可能
        console.warn('[PWA Auth] setPersistence failed:', e);
      }

      // 期待 uid でのログインを保証する関数
      const ensurePwaUid = async () => {
        if (exchangingRef.current) return;
        exchangingRef.current = true;
        try {
          const exchange = httpsCallable(functions, 'exchangePwaInstallation');
          // installId を渡して upsert + customToken発行
          const res = await exchange({ installId });
          const customToken = res?.data?.customToken;
          const newUid = res?.data?.uid;

          if (!customToken || !newUid) {
            throw new Error('Failed to issue custom token');
          }
          await signInWithCustomToken(auth, customToken);
          setUid(newUid);
          // HUD/ログ（任意）
          try { window.__HUD__?.log?.('[PWA Auth] signed in as', newUid); } catch {}
        } catch (e) {
          console.error('[PWA Auth] exchange/signIn failed:', e);
          setError(e);
          setUid(null);
        } finally {
          exchangingRef.current = false;
        }
      };

      unsub = onAuthStateChanged(auth, async (user) => {
        try {
          // 1) まだ未ログイン → 期待 uid でログイン
          if (!user) {
            setUid(undefined); // ローディング継続
            await ensurePwaUid();
            return;
          }

          // 2) いるけど PWA_ ではない（LINEなど）→ 切り替え
          if (!user.uid || !user.uid.startsWith('PWA_')) {
            try {
              await signOut(auth);
            } catch {}
            setUid(undefined); // 再ログイン中
            await ensurePwaUid();
            return;
          }

          // 3) 既に正しい uid でログイン済み
          setUid(user.uid);

          // （任意）fcmToken が後から取得できた場合に upsert したいならここで:
          // if (fcmToken && !exchangingRef.current) {
          //   exchangingRef.current = true;
          //   try {
          //     const exchange = httpsCallable(functions, 'exchangePwaInstallation');
          //     await exchange({ installId, fcmToken });
          //   } finally {
          //     exchangingRef.current = false;
          //   }
          // }
        } catch (e) {
          console.error('[PWA Auth] onAuthStateChanged handler error:', e);
          setError(e);
          setUid(null);
        }
      });
    })();

    return () => {
      try { unsub(); } catch {}
    };
  // fcmToken が変わっても基本方針は維持。必要に応じて upsert 再実行を有効化可。
  }, [installId]);

  return { uid, error };
}
